import type { Map as MapboxMap, MapOptions } from 'mapbox-gl';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';

/**
 * Delays before each extra style fetch. The original request is Mapbox's.
 * After these are used, a still-failing style load is reported.
 */
export const MAPBOX_STYLE_LOAD_RETRY_DELAYS_MS = [1_000, 2_000, 4_000] as const;

/**
 * Mapbox style JSON (`/styles/v1/{owner}/{style}`), not a sprite or tile URL
 * derived from that style.
 */
const MAPBOX_STYLE_DOCUMENT_PATTERN =
  /\/styles\/v1\/[^/?#]+\/[^/?#]+\/?(?:[?#]|$)/;

/** Browser fetch failures. HTTP status is classified separately. */
const TRANSIENT_FETCH_MESSAGE_PATTERN =
  /^(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource)\b/;

export type MapboxStyleLoadErrorEvent = {
  error?: unknown;
};

export type MapboxStyleReloadOptions = {
  diff?: boolean;
  config?: MapOptions['config'];
};

/** Subset of {@link mapboxgl.Map} used to recover a failed style fetch. */
export type MapboxStyleLoadRecoveryMap = {
  on: (
    type: 'error' | 'style.load',
    listener: (event: MapboxStyleLoadErrorEvent) => void,
  ) => void;
  off: (
    type: 'error' | 'style.load',
    listener: (event: MapboxStyleLoadErrorEvent) => void,
  ) => void;
  setStyle: (style: string, options?: MapboxStyleReloadOptions) => void;
};

export type MapboxStyleLoadRecoveryOptions = {
  /** Style URL reloaded after a transient style-document failure. */
  style: string;
  /** Fragment config from the original map, kept across the reload. */
  config?: MapOptions['config'];
  /**
   * Called for an error this helper does not recover from, and for a style
   * fetch that still fails after {@link MAPBOX_STYLE_LOAD_RETRY_DELAYS_MS}.
   * Defaults to `console.error`, which the Sentry console integration captures.
   */
  reportError?: (error: unknown) => void;
};

export type MapboxLoadErrorDecision =
  { type: 'ignore' } | { type: 'retry-style' } | { type: 'report' };

const readMessage = (error: unknown): string => {
  if (guards.is.string(error)) return error;
  if (guards.is.error(error)) return error.message;
  return '';
};

const readOwnField = (error: Error, key: string): unknown => {
  if (!guards.is.keyOf(error, key)) return undefined;
  return Reflect.get(error, key);
};

const readUrl = (error: unknown): string => {
  if (!guards.is.error(error)) return '';
  const url = readOwnField(error, 'url');
  return guards.is.string(url) ? url : '';
};

const readStatus = (error: unknown): number | undefined => {
  if (!guards.is.error(error)) return undefined;
  const status = readOwnField(error, 'status');
  return guards.is.number(status) ? status : undefined;
};

const readCause = (error: unknown): unknown => {
  if (!guards.is.error(error)) return undefined;
  return error.cause;
};

const isDomException = (error: unknown): error is DOMException =>
  error instanceof DOMException;

const isAbortError = (error: unknown): boolean => {
  if (guards.is.error(error)) return error.name === 'AbortError';
  // jsdom's DOMException is not an Error, but Mapbox still uses its name.
  if (isDomException(error)) return error.name === 'AbortError';
  return false;
};

const isTransientHttpStatus = (status: number): boolean =>
  status === 408 || status === 429 || (status >= 500 && status <= 599);

const failureText = (error: unknown): string => {
  const cause = readCause(error);
  return [
    readMessage(error),
    readUrl(error),
    readMessage(cause),
    readUrl(cause),
  ]
    .filter((part) => part.length > 0)
    .join(' ');
};

const isTransientFetchFailure = (error: unknown): boolean => {
  if (TRANSIENT_FETCH_MESSAGE_PATTERN.test(readMessage(error))) return true;
  return TRANSIENT_FETCH_MESSAGE_PATTERN.test(readMessage(readCause(error)));
};

/**
 * Decide what to do with one Mapbox `error` event.
 * A transient failure of the style document itself is retried. Cancelled
 * requests are ignored. Every other error, including a bad token or an
 * invalid layer, is reported.
 */
export const classifyMapboxLoadError = (
  error: unknown,
): MapboxLoadErrorDecision => {
  if (isAbortError(error)) return { type: 'ignore' };

  const status = readStatus(error) ?? readStatus(readCause(error));
  if (status !== undefined && !isTransientHttpStatus(status)) {
    return { type: 'report' };
  }

  const transient =
    status !== undefined
      ? isTransientHttpStatus(status)
      : isTransientFetchFailure(error);
  if (!transient) return { type: 'report' };
  if (MAPBOX_STYLE_DOCUMENT_PATTERN.test(failureText(error))) {
    return { type: 'retry-style' };
  }
  return { type: 'report' };
};

const defaultReportMapboxLoadError = (error: unknown): void => {
  console.error(error);
};

/**
 * Listen for Mapbox load errors and reload a style document that failed
 * because the network request did not complete.
 *
 * Mapbox writes an `error` event to `console.error` when nothing is
 * listening, and the Sentry console integration reports that line. While a
 * retry is still available the style-fetch failure is not reported. Tile,
 * sprite, auth, and style-validation errors are reported immediately.
 * Returns a function that removes the listeners and cancels a pending reload.
 */
export const bindMapboxStyleLoadRecovery = (
  map: MapboxStyleLoadRecoveryMap,
  options: MapboxStyleLoadRecoveryOptions,
): (() => void) => {
  const reportError = options.reportError ?? defaultReportMapboxLoadError;
  let retriesStarted = 0;
  let pendingRetry: ReturnType<typeof setTimeout> | undefined;
  let reportedTerminalFailure = false;
  let disposed = false;

  const onStyleLoad = () => {
    retriesStarted = 0;
    reportedTerminalFailure = false;
    if (pendingRetry === undefined) return;
    clearTimeout(pendingRetry);
    pendingRetry = undefined;
  };

  const scheduleStyleReload = (error: unknown) => {
    if (reportedTerminalFailure || pendingRetry !== undefined) return;
    const delay = MAPBOX_STYLE_LOAD_RETRY_DELAYS_MS[retriesStarted];
    if (delay === undefined) {
      reportedTerminalFailure = true;
      reportError(error);
      return;
    }
    retriesStarted += 1;
    // One in-flight reload. A second event from the same failed request must not stack timers.
    pendingRetry = setTimeout(() => {
      pendingRetry = undefined;
      if (disposed) return;
      map.setStyle(options.style, { diff: false, config: options.config });
    }, delay);
  };

  const onError = (event: MapboxStyleLoadErrorEvent) => {
    if (event.error === undefined) return;
    const decision = classifyMapboxLoadError(event.error);
    switch (decision.type) {
      case 'ignore':
        return;
      case 'retry-style':
        scheduleStyleReload(event.error);
        return;
      case 'report':
        reportError(event.error);
        return;
      default:
        assertUnreachable(decision);
    }
  };

  map.on('error', onError);
  map.on('style.load', onStyleLoad);
  return () => {
    disposed = true;
    if (pendingRetry !== undefined) clearTimeout(pendingRetry);
    map.off('error', onError);
    map.off('style.load', onStyleLoad);
  };
};

/**
 * {@link bindMapboxStyleLoadRecovery} for a Mapbox map instance.
 * Font fields are copied from the current map: `setStyle` replaces any
 * font option that is present, and the published options type requires them.
 */
export const bindMapboxGlStyleLoadRecovery = (
  map: MapboxMap,
  options: MapboxStyleLoadRecoveryOptions,
): (() => void) => {
  const styleLoadListeners = new Map<
    (event: MapboxStyleLoadErrorEvent) => void,
    () => void
  >();
  return bindMapboxStyleLoadRecovery(
    {
      on: (type, listener) => {
        switch (type) {
          case 'error':
            map.on('error', listener);
            return;
          case 'style.load': {
            // style.load carries no error payload; keep the listener identity for off().
            const wrapped = () => listener({});
            styleLoadListeners.set(listener, wrapped);
            map.on('style.load', wrapped);
            return;
          }
          default:
            assertUnreachable(type);
        }
      },
      off: (type, listener) => {
        switch (type) {
          case 'error':
            map.off('error', listener);
            return;
          case 'style.load': {
            const wrapped = styleLoadListeners.get(listener);
            if (!wrapped) return;
            styleLoadListeners.delete(listener);
            map.off('style.load', wrapped);
            return;
          }
          default:
            assertUnreachable(type);
        }
      },
      setStyle: (style, reloadOptions) => {
        map.setStyle(style, {
          diff: reloadOptions?.diff,
          config: reloadOptions?.config,
          localFontFamily: map._localFontFamily,
          localIdeographFontFamily: map._localIdeographFontFamily,
        });
      },
    },
    options,
  );
};
